import {useCallback, useEffect, useRef, useState} from 'react';
import {
  useAsaTracking,
  useCioAsaContext,
  type AssistantSubmitSource,
  type ResultGroupMeta,
  type SearchResultEventRequest,
  type UseAsaResultsOptions,
} from '@constructor-io/constructorio-ui-asa';
import {formatLocalTime} from '../timing';
import {appendBlock, type TweChatMessage, type TweResultGroup} from './assistantModel';
export type {TweChatMessage} from './assistantModel';

export interface TweAsaResultsOptions extends UseAsaResultsOptions {
  initialMessages?: TweChatMessage[];
  /** Called once at Constructor's native onResultLoadStart boundary. */
  onResultLoadStart?: (payload: {intent: string; intentResultId?: string; threadId?: string}) => void;
}

type SubmitSource = AssistantSubmitSource | 'follow-up-question';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The JavaScript client documents the event but not a dedicated payload type.
 * Accept the documented collection shape plus common string/object encodings,
 * while ignoring malformed entries rather than disrupting the active turn.
 */
function questionsFromEvent(data: unknown): string[] {
  const values = Array.isArray(data)
    ? data
    : isRecord(data) && Array.isArray(data.questions)
      ? data.questions
      : isRecord(data) && Array.isArray(data.follow_up_questions)
        ? data.follow_up_questions
        : [];

  return values.flatMap(value => {
    if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
    if (!isRecord(value)) return [];
    const question = typeof value.question === 'string'
      ? value.question
      : typeof value.text === 'string'
        ? value.text
        : typeof value.value === 'string'
          ? value.value
          : '';
    return question.trim() ? [question.trim()] : [];
  });
}

function withoutInternalRequestFields(request: Record<string, unknown>): SearchResultEventRequest {
  const {features: _features, feature_variants: _featureVariants, ...publicRequest} = request;
  return publicRequest;
}

/**
 * A narrow compatibility wrapper around the current official useAsaResults
 * behavior. It retains all official message/thread/group semantics and adds
 * only client-declared `follow_up_questions`, which ASA UI 1.5.0 does not yet
 * expose as a message field.
 */
export function useTweAsaResults(options?: TweAsaResultsOptions) {
  const [messages, setMessages] = useState<TweChatMessage[]>(() => options?.initialMessages || []);
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesRef = useRef(messages);
  const isStreamingRef = useRef(false);
  const killSwitchRef = useRef(false);
  const readerRef = useRef<ReadableStreamDefaultReader<{type?: string; data?: unknown}> | null>(null);
  const threadIdRef = useRef(options?.initialThreadId ?? null);
  const idCounterRef = useRef(0);
  const contextValue = useCioAsaContext();

  if (!contextValue) throw new Error('useTweAsaResults must be used within a CioAsaProvider.');
  const {cioClient, staticRequestConfigs, callbacks, section} = contextValue;
  const {domain} = staticRequestConfigs || {};
  if (!cioClient || !domain) throw new Error('useTweAsaResults requires a configured cioClient and domain.');

  const tracking = useAsaTracking({tracker: cioClient.tracker, section, threadId: threadIdRef.current ?? undefined});
  const trackingRef = useRef(tracking);
  const callbacksRef = useRef(callbacks);
  const staticRequestConfigsRef = useRef(staticRequestConfigs);
  const onResultLoadStartRef = useRef(options?.onResultLoadStart);
  trackingRef.current = tracking;
  callbacksRef.current = callbacks;
  staticRequestConfigsRef.current = staticRequestConfigs;
  onResultLoadStartRef.current = options?.onResultLoadStart;

  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const nextMessageId = useCallback(() => {
    idCounterRef.current += 1;
    return `msg-${idCounterRef.current}-${Date.now()}`;
  }, []);

  const updateAssistant = useCallback((assistantId: string, updater: (message: TweChatMessage) => TweChatMessage) => {
    setMessages(previous => previous.map(message => message.id === assistantId ? updater(message) : message));
  }, []);

  const startStream = useCallback((rawText: string, source: SubmitSource, retryAssistantId?: string) => {
    const intent = rawText.trim();
    if (!intent || isStreamingRef.current) return;

    const trackingSource: AssistantSubmitSource = source === 'follow-up-question' ? 'suggestion' : source;
    const submittedAt = formatLocalTime();
    const responseStartedAt = performance.now();
    trackingRef.current.trackSubmit(intent);
    callbacksRef.current?.onAssistantSubmit?.({intent, source: trackingSource});

    let assistantId = retryAssistantId;
    if (retryAssistantId) {
      const failed = messagesRef.current.find(message => message.id === retryAssistantId && message.role === 'assistant');
      if (!failed?.intent) return;
      assistantId = failed.id;
      setMessages(previous => previous.map(message => message.id === assistantId ? {
        ...message,
        text: '',
        groups: [],
        blocks: [],
        renderAttempt: (message.renderAttempt || 0) + 1,
        intentResultId: undefined,
        refinement: undefined,
        followUpQuestions: undefined,
        status: 'loading',
        responseStartedAt,
        responseDurationMs: undefined,
        completedAt: undefined,
      } : message));
    } else {
      const userMessage: TweChatMessage = {id: nextMessageId(), role: 'user', text: intent, status: 'done', submittedAt};
      const assistantMessage: TweChatMessage = {id: nextMessageId(), role: 'assistant', text: '', groups: [], blocks: [], status: 'loading', intent, responseStartedAt};
      assistantId = assistantMessage.id;
      setMessages(previous => [...previous, userMessage, assistantMessage]);
    }

    if (!assistantId) return;
    setIsStreaming(true);
    isStreamingRef.current = true;
    killSwitchRef.current = false;

    const agentParams = {...staticRequestConfigsRef.current};
    delete agentParams.intent;
    delete agentParams.threadId;
    const stream = cioClient.agent.getAgentResultsStream(intent, {
      ...agentParams,
      domain,
      ...(threadIdRef.current && {threadId: threadIdRef.current}),
    });
    const reader = stream.getReader() as ReadableStreamDefaultReader<{type?: string; data?: unknown}>;
    readerRef.current = reader;

    const streamState: {pendingGroup: ResultGroupMeta | null; intentResultId?: string; loadStartFired: boolean; groupCount: number} = {
      pendingGroup: null,
      intentResultId: undefined,
      loadStartFired: false,
      groupCount: 0,
    };
    const patchAssistant = (patch: Partial<TweChatMessage>) => updateAssistant(assistantId, message => ({...message, ...patch}));
      const fireLoadStart = () => {
        if (streamState.loadStartFired) return;
        streamState.loadStartFired = true;
        const payload = {intent, intentResultId: streamState.intentResultId, threadId: threadIdRef.current || undefined};
        trackingRef.current.trackResultLoadStarted(payload);
        callbacksRef.current?.onResultLoadStart?.(payload);
        onResultLoadStartRef.current?.(payload);
      };

    void (async () => {
      try {
        while (!killSwitchRef.current) {
          const result = await reader.read();
          if (killSwitchRef.current) break;
          if (result.done) {
            patchAssistant({
              status: 'done',
              responseDurationMs: Math.max(0, performance.now() - responseStartedAt),
              completedAt: formatLocalTime(),
              responseStartedAt: undefined,
            });
            const payload = {intent, searchResultCount: streamState.groupCount, intentResultId: streamState.intentResultId};
            trackingRef.current.trackResultLoadFinished(payload);
            callbacksRef.current?.onResultLoadFinish?.(payload);
            break;
          }

          const {type, data} = result.value;
          const eventData = isRecord(data) ? data : {};
          if (!streamState.intentResultId && typeof eventData.intent_result_id === 'string') {
            streamState.intentResultId = eventData.intent_result_id;
            patchAssistant({intentResultId: streamState.intentResultId});
          }

          if (type === 'start') {
            if (typeof eventData.thread_id === 'string') {
              threadIdRef.current = eventData.thread_id;
              patchAssistant({threadId: eventData.thread_id});
            }
            fireLoadStart();
          } else if (type === 'group') {
            streamState.pendingGroup = {
              display_name: typeof eventData.display_name === 'string' ? eventData.display_name : typeof eventData.group === 'string' ? eventData.group : '',
              value: typeof eventData.value === 'string' ? eventData.value : typeof eventData.group === 'string' ? eventData.group : '',
            };
          } else if (type === 'search_result') {
            fireLoadStart();
            streamState.groupCount += 1;
            const response = isRecord(eventData.response) ? eventData.response : {};
            const searchRequest = isRecord(response.search_request) ? response.search_request : {};
            const baseGroup = streamState.pendingGroup ?? {
              display_name: typeof searchRequest.display_name === 'string' ? searchRequest.display_name : typeof eventData.title === 'string' ? eventData.title : '',
              value: typeof searchRequest.search_term === 'string' ? searchRequest.search_term : typeof eventData.title === 'string' ? eventData.title : '',
            };
            const request = isRecord(eventData.request) ? withoutInternalRequestFields(eventData.request) : undefined;
            const group: TweResultGroup = {
              group: {...baseGroup, ...(request && {data: {...baseGroup.data, request}})},
              searchResults: Array.isArray(response.results) ? response.results.filter(isRecord) : Array.isArray(eventData.results) ? eventData.results.filter(isRecord) : [eventData],
              ...(typeof eventData.title === 'string' && {originalTitle: eventData.title}),
              ...(typeof eventData.text === 'string' && {description: eventData.text}),
              ...(typeof eventData.result_id === 'string' && {searchResultId: eventData.result_id}),
              ...(typeof eventData.intent_result_id === 'string' && {intentResultId: eventData.intent_result_id}),
            };
            updateAssistant(assistantId, message => ({...message, status: 'streaming', blocks: appendBlock(message, {type: 'search_result', groupIndex: message.groups?.length || 0}), groups: [...(message.groups || []), group]}));
            streamState.pendingGroup = null;
          } else if (type === 'message') {
            fireLoadStart();
            const text = typeof eventData.text === 'string' ? eventData.text : '';
            updateAssistant(assistantId, message => ({...message, status: 'streaming', text: message.text + text, blocks: appendBlock(message, {type: 'message', text})}));
          } else if (type === 'follow_up_refinement') {
            fireLoadStart();
            const question = typeof eventData.question === 'string' ? eventData.question.trim() : '';
            const options = Array.isArray(eventData.options) ? eventData.options.filter((option): option is string => typeof option === 'string' && option.trim() !== '') : [];
            if (question && options.length) updateAssistant(assistantId, message => ({...message, status: 'streaming', refinement: {question, options}, blocks: appendBlock(message, {type: 'follow_up_refinement', question, options})}));
          } else if (type === 'follow_up_questions') {
            fireLoadStart();
            const followUpQuestions = questionsFromEvent(data);
            if (followUpQuestions.length) updateAssistant(assistantId, message => ({...message, status: 'streaming', followUpQuestions, blocks: appendBlock(message, {type: 'follow_up_questions', questions: followUpQuestions})}));
          } else if (type === 'server_error') {
            patchAssistant({status: 'error', responseStartedAt: undefined});
            break;
          }
        }
      } catch {
        patchAssistant({status: 'error', responseStartedAt: undefined});
      } finally {
        if (readerRef.current === reader) {
          void reader.cancel().catch(() => undefined);
          readerRef.current = null;
          setIsStreaming(false);
          isStreamingRef.current = false;
        }
      }
    })();
  }, [cioClient, domain, nextMessageId, updateAssistant]);

  const sendMessage = useCallback((text: string, source: SubmitSource = 'input') => startStream(text, source), [startStream]);
  const retryMessage = useCallback((assistantId: string) => {
    const failed = messagesRef.current.find(message => message.id === assistantId && message.role === 'assistant');
    if (failed?.intent) startStream(failed.intent, 'input', failed.id);
  }, [startStream]);

  useEffect(() => () => {
    killSwitchRef.current = true;
    if (readerRef.current) {
      void readerRef.current.cancel().catch(() => undefined);
      readerRef.current = null;
    }
  }, []);

  const clearHistory = useCallback(() => {
    killSwitchRef.current = true;
    if (readerRef.current) {
      void readerRef.current.cancel().catch(() => undefined);
      readerRef.current = null;
    }
    threadIdRef.current = null;
    messagesRef.current = [];
    setMessages([]);
    setIsStreaming(false);
    isStreamingRef.current = false;
  }, []);

  return {messages, sendMessage, retryMessage, isStreaming, clearHistory};
}
